import React from 'react';
import { CellProps, Column } from 'react-table';
import {
    FinanceCreatorPaymentOutstanding,
    getOutstandingCreatorPayments,
    InferPaginatedFetchFunctionResponse,
    PaymentMethods,
    PaymentTerms,
} from '@round/api';
import Badge from '../../../../../ui/Badge/Badge';
import Table from '../../../../../ui/Table/Table';
import styles from './CreatorPaymentsTable.module.css';
import cn from 'classnames';
import { asMoney, formatDate } from '../../../../../utils/utits';
import useNonNullContext from '../../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../../contexts/OptionsContext';
import Copyable from './Copyable/Copyable';
import { Button } from '@round/ui-kit';
import { PopoverOnOverflow } from '@round/ui-kit';
import { renderTotalsAsMoney } from '../../../../booking/TableCells/helpers';
import Skeleton from '../../../../../ui/Skeleton/Skeleton';
import range from 'lodash/range';
import PaymentTermsPicker from '../../../../booking/TableCells/PaymentTermsPicker/PaymentTermsPicker';

type CreatorPaymentsTableProps = {
    data: FinanceCreatorPaymentOutstanding[];
    totals:
        | InferPaginatedFetchFunctionResponse<typeof getOutstandingCreatorPayments>['data']['payment_amount_total']
        | null;
    loading: boolean;
    onPaidByClicked?: (paymentId: number, method: PaymentMethods) => void;
    updatePaymentTerms: (bookingId: number, paymentTerms: PaymentTerms) => void;
};

const CreatorPaymentsTable = ({
    data,
    onPaidByClicked,
    updatePaymentTerms,
    totals,
    loading,
}: CreatorPaymentsTableProps) => {
    const { currencies } = useNonNullContext(OptionsContext);

    const handlePaidByButtonClicked = (id: number, method: PaymentMethods) => {
        if (typeof onPaidByClicked === 'function') {
            onPaidByClicked(id, method);
        }
    };

    const columns: Column<FinanceCreatorPaymentOutstanding>[] = [
        {
            Header: 'ID',
            accessor: 'id',
        },
        {
            Header: 'Campaign Name',
            accessor: 'campaign_name',
            Cell: ({ value }) => (
                <PopoverOnOverflow
                    popoverContent={<div className={styles.tooltip}>{value}</div>}
                    popoverOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 10] } }] }}
                >
                    {(setRef) => (
                        <Badge ref={setRef} className={cn(styles.badge, styles.info, styles.campaignName)}>
                            {value}
                        </Badge>
                    )}
                </PopoverOnOverflow>
            ),
        },
        {
            Header: 'Creator',
            accessor: 'influencer_name',
            Cell: ({ value }) => <Badge className={cn(styles.badge, styles.info)}>{value}</Badge>,
        },
        {
            Header: 'Manager',
            accessor: 'manager_email',
            Cell: ({ value }) => {
                if (!value) {
                    return null;
                }

                return <Badge className={cn(styles.badge, styles.info)}>{value}</Badge>;
            },
        },
        {
            Header: 'Posted At',
            accessor: 'posted_at',
            Cell: ({ value: postedAt }) => (
                <>
                    {typeof postedAt === 'number' && postedAt > 0
                        ? formatDate(new Date(postedAt * 1000).toISOString(), { month: 'numeric' })
                        : ''}
                </>
            ),
        },
        {
            Header: 'Due Date',
            accessor: 'payment_due_at',
            Cell: ({ value: paymentDueAt }) => (
                <>
                    {typeof paymentDueAt === 'number' && paymentDueAt > 0
                        ? formatDate(new Date(paymentDueAt * 1000).toISOString(), { month: 'numeric' })
                        : ''}
                </>
            ),
        },
        {
            Header: 'Payment Terms',
            accessor: 'payment_terms',
            Cell: ({ value, row: { original } }) => (
                <PaymentTermsPicker
                    value={value}
                    onChange={async (paymentTerms) => updatePaymentTerms(original.id, paymentTerms)}
                />
            ),
        },
        {
            Header: 'Booking Amount',
            accessor: 'price',
            Cell: ({
                value,
                row: {
                    original: { currency },
                },
            }) => (
                <>
                    {asMoney(
                        value,
                        currencies.find((c) => c.id === currency)
                    )}
                </>
            ),
        },
        {
            Header: 'Payment Amount',
            accessor: 'influencer_payment_amount',
            Cell: ({
                value,
                row: {
                    original: { currency },
                },
            }) => (
                <>
                    {asMoney(
                        value,
                        currencies.find((c) => c.id === currency)
                    )}
                </>
            ),
        },
        {
            Header: 'PayPal Email',
            accessor: 'influencer_paypal_email',
            Cell: ({ value }) => (value ? <Copyable className={styles.copyable}>{value}</Copyable> : <>-</>),
        },
        {
            Header: 'PayPal Ref',
            accessor: 'payment_reference_paypal',
            Cell: ({ value }) => (value ? <Copyable className={styles.copyable}>{value}</Copyable> : <>-</>),
        },
        {
            Header: 'Bank Name',
            accessor: 'influencer_bank_name',
            Cell: ({ value }) => (value ? <Copyable className={styles.copyable}>{value}</Copyable> : <>-</>),
        },
        {
            Header: 'Bank Acc. No.',
            accessor: 'influencer_bank_account_number',
            Cell: ({ value }) => (value ? <Copyable className={styles.copyable}>{value}</Copyable> : <>-</>),
        },
        {
            Header: 'Bank Sort Code',
            accessor: 'influencer_bank_sort_code',
            Cell: ({ value }) => (value ? <Copyable className={styles.copyable}>{value}</Copyable> : <>-</>),
        },
        {
            Header: 'Bank Ref',
            accessor: 'payment_reference_bank',
            Cell: ({ value }) => (value ? <Copyable className={styles.copyable}>{value}</Copyable> : <>-</>),
        },
        {
            Header: 'Paid By',
            id: 'paidBy',
            Cell: ({
                row: {
                    original: { id, influencer_preferred_payment_method },
                },
            }: CellProps<FinanceCreatorPaymentOutstanding>) => {
                return (
                    <div className={styles.paidByButtonsContainer}>
                        <Button
                            type="filled"
                            className={cn(styles.paidByButton, {
                                [styles.preferred]: influencer_preferred_payment_method === 'paypal',
                            })}
                            onClick={() => handlePaidByButtonClicked(id, 'paypal')}
                        >
                            PayPal
                        </Button>
                        <Button
                            type="filled"
                            className={cn(styles.paidByButton, {
                                [styles.preferred]: influencer_preferred_payment_method === 'bank_transfer',
                            })}
                            onClick={() => handlePaidByButtonClicked(id, 'bank_transfer')}
                        >
                            Bank
                        </Button>
                    </div>
                );
            },
        },
    ];

    return (
        <Table
            className={styles.table}
            columns={columns}
            data={data}
            renderRow={(row) => {
                const keyword =
                    row.original.influencer_preferred_payment_method === 'bank_transfer' ? 'bank' : 'paypal';

                const highlightedColumns = columns
                    .filter((col) => {
                        const accessor = col.accessor as string;
                        return accessor?.includes('influencer_payment_amount') || accessor?.includes(keyword);
                    })
                    .map((col) => col.accessor as string);

                return (
                    <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                            <td
                                {...cell.getCellProps()}
                                className={cn({
                                    [styles.highlighted]: highlightedColumns.includes(cell.column.id),
                                })}
                            >
                                {cell.render('Cell')}
                            </td>
                        ))}
                    </tr>
                );
            }}
            renderBody={({ rows, prepareRow, columns }, renderRow) => (
                <tbody>
                    {rows.map((row) => {
                        prepareRow(row);
                        return renderRow(row);
                    })}
                    {loading &&
                        range(0, 10).map((i) => (
                            <tr key={i}>
                                {columns.map((column) => (
                                    <td key={column.id}>
                                        <Skeleton style={{ zIndex: 'unset' }} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                </tbody>
            )}
            renderFooter={() => (
                <tfoot>
                    <tr>
                        <td className={styles.stickyFooterCell} colSpan={3} />
                        <td colSpan={5} />
                        <td>{totals ? renderTotalsAsMoney(totals, currencies) : '-'}</td>
                        <td colSpan={7} />
                    </tr>
                </tfoot>
            )}
        />
    );
};

export default CreatorPaymentsTable;
